import React, { useState, useEffect, useRef, useCallback } from "react";
import { Core, ElementDefinition } from "cytoscape";
import { ScrollToTop } from "../../components/ScrollToTop";
import { useTheme } from "../../theme/useTheme";
import styles from "./PathwayExplorer.module.css";
import { useNetworkData } from "../../hooks/useNetworkData";
import {
  PathwayConfigurationForm,
  PathwayFormSkeleton,
} from "../../components/PathwayExplorer/PathwayConfigurationForm";
import { TabNavigation } from "../../components/PathwayExplorer/TabNavigation";
import { DirectNeighborGraph } from "../../components/PathwayExplorer/DirectNeighborGraph";
import { KShortestPaths } from "../../components/PathwayExplorer/KShortestPaths";
import { PathwayComparison } from "../../components/PathwayExplorer/PathwayComparison";
import {
  ActiveTab,
  PathwayAnalysisOption,
  PathwayOption,
  PathwayTypeOption,
} from "../../components/PathwayExplorer/types/pathway.types";
import { pathwayExplorerClient } from "../../services/PathwayExplorerClient";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";
import { XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/outline";

const edgeTypeOptions: string[] = [
  "INTERACTS_WITH",
  "ASSOCIATED",
  "CO_EXPRESSED",
  "CO_OCCURS",
  "UP_REGULATES",
  "DOWN_REGULATES",
  "PHYSICALLY_INTERACTS",
  "REGULATES_TRANSCRIPTION",
  "REPRESSES_TRANSCRIPTION",
  "ACTIVATES_TRANSCRIPTION",
  "ACTS_ON",
];

export const PathwayExplorer: React.FC = () => {
  const cyRef = useRef<Core | null>(null);
  const layoutInitialized = useRef<boolean>(false);

  const { setLoading } = useTheme();

  const [pathwayAnalysisOptions, setPathwayAnalysisOptions] = useState<PathwayAnalysisOption[]>([]);
  const [isLoadingPathwayAnalysis, setIsLoadingPathwayAnalysis] = useState(true);
  const [pathwayDatasetsLoadError, setPathwayDatasetsLoadError] = useState<string | null>(null);
  const [selectedPathwayAnalysis, setSelectedPathwayAnalysis] = useState<string>("");
  const [pathwayTypeOptions, setPathwayTypeOptions] = useState<PathwayTypeOption[]>([]);
  const [isLoadingPathwayTypes, setIsLoadingPathwayTypes] = useState<boolean>(false);
  const [pathwayOptions, setPathwayOptions] = useState<PathwayOption[]>([]);
  const [isLoadingPathways, setIsLoadingPathways] = useState<boolean>(false);
  const [selectedPathway, setSelectedPathway] = useState<string>("");
  const [edgeType, setEdgeType] = useState<string>("INTERACTS_WITH");
  const [minEdgeScore, setMinEdgeScore] = useState<number>(0);
  const [pathwayType, setPathwayType] = useState<string>("Signalling");
  const [cytoscapeElements, setCytoscapeElements] = useState<ElementDefinition[]>([]);

  const [isFormOpen, setIsFormOpen] = useState(true);
  const [isPlotControlsOpen, setIsPlotControlsOpen] = useState(true);

  const [activeTab, setActiveTab] = useState<ActiveTab>("direct-neighbor");
  const [showTabs, setShowTabs] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const tabAreaRef = useRef<HTMLDivElement>(null);
  const shouldScrollToTabAreaRef = useRef(false);

  const [selectedGeneToHighlight, setSelectedGeneToHighlight] = useState<string>("none");
  const [highlightColumn, setHighlightColumn] = useState<string>("none");
  const [highlightedGenesList, setHighlightedGenesList] = useState<string[]>([]);
  const [splitComponents, setSplitComponents] = useState<boolean>(false);
  const [labelFontSize, setLabelFontSize] = useState<number>(9);
  const [pathwayGeneList, setPathwayGeneList] = useState<string[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);

  const { networkData, isPlottingNetwork, plotError, setPlotError, plotNetwork } = useNetworkData();
  const errorMessage = plotError ?? apiError;
  const clearError = () => {
    setApiError(null);
    setPlotError(null);
  };

  const clearPathwayDatasetsError = () => {
    setPathwayDatasetsLoadError(null);
  };

  const loadPathwayDatasets = useCallback(async () => {
    setApiError(null);
    setLoading(true);
    setIsLoadingPathwayAnalysis(true);
    try {
      const datasets = await pathwayExplorerClient.getAvailableDatasets();

      const mappedOptions: PathwayAnalysisOption[] = datasets.map((datasetName) => ({
        id: datasetName,
        label: datasetName,
      }));

      setPathwayDatasetsLoadError(null);
      setPathwayAnalysisOptions(mappedOptions);
      setSelectedPathwayAnalysis((prev) =>
        mappedOptions.length > 0 && !prev ? mappedOptions[0].id : prev
      );
    } catch (error) {
      console.error("Error fetching pathway datasets:", error);
      setPathwayDatasetsLoadError(getApiErrorMessage(error));
      setPathwayAnalysisOptions([]);
      setSelectedPathwayAnalysis("");
    } finally {
      setIsLoadingPathwayAnalysis(false);
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    loadPathwayDatasets();
  }, [loadPathwayDatasets]);

  useEffect(() => {
    const fetchPathwayTypes = async () => {
      if (!selectedPathwayAnalysis) {
        setPathwayTypeOptions([]);
        return;
      }

      setApiError(null);
      try {
        setIsLoadingPathwayTypes(true);
        const types = await pathwayExplorerClient.getAvailablePathwayTypes(selectedPathwayAnalysis);

        if (types && Array.isArray(types)) {
          const mappedOptions: PathwayTypeOption[] = types.map(type => ({
            value: type,
            label: type,
          }));

          setPathwayTypeOptions(mappedOptions);

          if (mappedOptions.length > 0) {
            setPathwayType(mappedOptions[0].value);
          }
        } else {
          setPathwayTypeOptions([]);
        }
      } catch (error) {
        console.error('Error fetching pathway types:', error);
        setPathwayTypeOptions([]);
        setApiError(getApiErrorMessage(error));
      } finally {
        setIsLoadingPathwayTypes(false);
      }
    };

    fetchPathwayTypes();
  }, [selectedPathwayAnalysis]);

  useEffect(() => {
    const fetchPathways = async () => {
      if (!selectedPathwayAnalysis) {
        setPathwayOptions([]);
        setSelectedPathway("");
        return;
      }

      if (isLoadingPathwayTypes) {
        return;
      }

      if (pathwayTypeOptions.length > 0 && !pathwayType) {
        return;
      }

      setApiError(null);
      try {
        setIsLoadingPathways(true);
        setSelectedPathway("");

        const params: any = {
          dataset: selectedPathwayAnalysis,
        };

        if (pathwayTypeOptions.length > 0 && pathwayType) {
          params.pathway_type = pathwayType;
        }

        const pathways = await pathwayExplorerClient.getAvailablePathways(params);

        if (pathways && Array.isArray(pathways)) {
          const mappedOptions: PathwayOption[] = pathways.map(pathway => ({
            value: pathway,
            label: pathway,
          }));

          setPathwayOptions(mappedOptions);

          if (mappedOptions.length > 0) {
            setSelectedPathway(mappedOptions[0].value);
          }
        } else {
          setPathwayOptions([]);
          setSelectedPathway("");
        }
      } catch (error) {
        console.error('Error fetching pathways:', error);
        setPathwayOptions([]);
        setSelectedPathway("");
        setApiError(getApiErrorMessage(error));
      } finally {
        setIsLoadingPathways(false);
      }
    };

    fetchPathways();
  }, [selectedPathwayAnalysis, pathwayType, isLoadingPathwayTypes, pathwayTypeOptions.length]);

  useEffect(() => {
    if (!networkData) {
      setCytoscapeElements([]);
      layoutInitialized.current = false;
      return;
    }

    // Sync plot controls to initial graph defaults so drawer and graph match on first draw
    setLabelFontSize(9);

    const nodeElements: ElementDefinition[] = networkData.nodes.map((node) => ({
      data: {
        id: node.id,
        label: node.label,
      },
    }));

    const nodeIdSet = new Set(networkData.nodes.map((n) => n.id));
    const edgeElements: ElementDefinition[] = networkData.edges
      .filter((edge) => nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target))
      .map((edge, index) => ({
        data: {
          id: `edge-${index}-${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target,
          label: edgeType,
        },
      }));

    setCytoscapeElements([...nodeElements, ...edgeElements]);
    layoutInitialized.current = false;
  }, [networkData]);

  useEffect(() => {
    if (cyRef.current) {
      setTimeout(() => {
        if (cyRef.current) {
          cyRef.current.resize();
          cyRef.current.fit(undefined, 50);
          cyRef.current.center();
        }
      }, 350);
    }
  }, [isPlotControlsOpen]);

  const handlePlotNetwork = async () => {
    try {
      const genes = await pathwayExplorerClient.getPathwayGenes({
        pathway: selectedPathway,
        dataset: selectedPathwayAnalysis,
      });

      const geneList: string[] = Array.isArray(genes)
        ? genes.map((g: any) => (typeof g === "string" ? g : g.gene_symbol || g.gene_id))
        : [];

      if (geneList.length === 0) {
        console.warn("No genes found for the selected pathway");
        return;
      }

      setPathwayGeneList(geneList);
      const neighborData = await plotNetwork(edgeType, geneList, minEdgeScore);
      if (neighborData) {
        setIsFormOpen(false);
      }
      setHighlightColumn("none");
      setSelectedGeneToHighlight("none");
      setSplitComponents(false);
      setLabelFontSize(9);
      setShowTabs(true);
      setActiveTab("direct-neighbor");
      shouldScrollToTabAreaRef.current = true;
      } catch (error) {
        console.error("Error during plot network:", error);
        setApiError(getApiErrorMessage(error));
    }
  };

  useEffect(() => {
    setSelectedGeneToHighlight("none");
  }, [selectedPathway]);

  useEffect(() => {
    if (showTabs && networkData && shouldScrollToTabAreaRef.current && tabAreaRef.current) {
      shouldScrollToTabAreaRef.current = false;
      requestAnimationFrame(() => {
        tabAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [showTabs, networkData]);

  useEffect(() => {
    const fetchHighlightedGenes = async () => {
      if (!selectedPathwayAnalysis || !selectedPathway || highlightColumn === "none" || !highlightColumn) {
        setHighlightedGenesList([]);
        return;
      }
      setApiError(null);
      try {
        const genes = await pathwayExplorerClient.getHighlightedGenes({
          dataset: selectedPathwayAnalysis,
          pathway: selectedPathway,
          highlight_col: highlightColumn,
        });
        const geneList = genes.map((g: any) => typeof g === "string" ? g : g.gene_symbol || g.gene_id || g);
        setHighlightedGenesList(geneList);
      } catch (error) {
        console.error("Error fetching highlighted genes:", error);
        setHighlightedGenesList([]);
        setApiError(getApiErrorMessage(error));
      }
    };
    fetchHighlightedGenes();
  }, [selectedPathwayAnalysis, selectedPathway, highlightColumn]);

  return (
    <div className="relative h-full flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div
        ref={containerRef}
        className={`h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-slate-600 ${styles.scrollContainer}`}
      >
        {pathwayDatasetsLoadError && (
          <div
            className="flex items-center gap-3 px-4 py-3 bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-sm"
            role="alert"
          >
            <span className="min-w-0 flex-1 font-medium whitespace-pre-wrap break-words">
              Could not load pathway analysis types: {pathwayDatasetsLoadError}
            </span>
            <button
              type="button"
              onClick={() => void loadPathwayDatasets()}
              disabled={isLoadingPathwayAnalysis}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoadingPathwayAnalysis ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : null}
              {isLoadingPathwayAnalysis ? "Retrying…" : "Try again"}
            </button>
            <button
              type="button"
              onClick={clearPathwayDatasetsError}
              className="shrink-0 p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-300 transition-colors"
              aria-label="Dismiss"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
        {errorMessage && (
          <div
            className="flex items-center gap-3 px-4 py-3 bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-sm"
            role="alert"
          >
            <span className="flex-1 font-medium">{errorMessage}</span>
            <button
              type="button"
              onClick={clearError}
              className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-300 transition-colors"
              aria-label="Dismiss error"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
        {isLoadingPathwayAnalysis && !pathwayDatasetsLoadError && (
          <div className="sticky top-0 z-40 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="px-6 py-6 bg-gradient-to-b from-gray-50 to-white dark:from-slate-800/50 dark:to-slate-800 border-t border-gray-100 dark:border-slate-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Loading pathway analysis options…
              </p>
              <PathwayFormSkeleton />
            </div>
          </div>
        )}
        {!isLoadingPathwayAnalysis && pathwayAnalysisOptions.length > 0 && (
          <PathwayConfigurationForm
            isFormOpen={isFormOpen}
            setIsFormOpen={setIsFormOpen}
            selectedPathwayAnalysis={selectedPathwayAnalysis}
            setSelectedPathwayAnalysis={setSelectedPathwayAnalysis}
            selectedPathway={selectedPathway}
            setSelectedPathway={setSelectedPathway}
            edgeType={edgeType}
            setEdgeType={setEdgeType}
            minEdgeScore={minEdgeScore}
            setMinEdgeScore={setMinEdgeScore}
            pathwayType={pathwayType}
            setPathwayType={setPathwayType}
            isPlottingNetwork={isPlottingNetwork}
            onPlotNetwork={handlePlotNetwork}
            pathwayAnalysisOptions={pathwayAnalysisOptions}
            pathwayOptions={pathwayOptions}
            edgeTypeOptions={edgeTypeOptions}
            pathwayTypeOptions={pathwayTypeOptions}
            isLoadingPathwayTypes={isLoadingPathwayTypes}
            isLoadingPathways={isLoadingPathways}
          />
        )}

        {networkData && (
          <div className="flex-1">
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg overflow-hidden">
              <div ref={tabAreaRef} className="flex flex-col">
                {showTabs && (
                  <>
                    <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

                    <div className="flex-1">
                      {activeTab === "direct-neighbor" && (
                        <DirectNeighborGraph
                          networkData={networkData}
                          cytoscapeElements={cytoscapeElements}
                          edgeType={edgeType}
                          isPlotControlsOpen={isPlotControlsOpen}
                          setIsPlotControlsOpen={setIsPlotControlsOpen}
                          selectedGeneToHighlight={selectedGeneToHighlight}
                          setSelectedGeneToHighlight={setSelectedGeneToHighlight}
                          highlightColumn={highlightColumn}
                          setHighlightColumn={setHighlightColumn}
                          datasetId={selectedPathwayAnalysis}
                          highlightedGenesList={highlightedGenesList}
                          labelFontSize={labelFontSize}
                          setLabelFontSize={setLabelFontSize}
                          splitComponents={splitComponents}
                          setSplitComponents={setSplitComponents}
                          cyRef={cyRef}
                          layoutInitialized={layoutInitialized}
                          selectedPathwayLabel={
                            pathwayOptions.find((o) => o.value === selectedPathway)?.label ??
                            selectedPathway
                          }
                        />
                      )}
                      {activeTab === "k-shortest-paths" && (
                        <KShortestPaths
                          geneList={pathwayGeneList}
                          edgeType={edgeType}
                          minEdgeScore={minEdgeScore}
                        />
                      )}
                      {activeTab === "pathway-comparison" && (
                          <PathwayComparison
                            pathwayOptions={pathwayOptions}
                            selectedPathway={selectedPathway}
                            datasetId={selectedPathwayAnalysis}
                            edgeType={edgeType}
                            minEdgeScore={minEdgeScore}
                          />
                        )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ScrollToTop scrollContainerRef={containerRef} />

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PathwayExplorer;
